import { useBookingModuleStore, useBookingModuleStoreActions, useCalendarRoot } from "PFApp/booking/store";
import { useCalendarRootMeta } from "PFApp/booking/store/provider/calendar_root_rows/use_calendar_root_meta";
import { useWorkforceMetricsData } from "PFApp/booking/store/provider/calendar_root_rows/use_workforce_metrics_data";
import { Tabs } from "PFApp/booking/types";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { usePinnedProjects } from "PFCore/hooks/queries/bookings/projects/use_pinned_projects";
import { ProjectsResponse } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { usePinnedWorkforceMembers } from "PFCore/hooks/queries/bookings/workforce/use_pinned_workforce_members";
import { WorkforceResponse } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useEffect } from "react";

import { useProjectsData } from "./use_projects_data";
import { useWorkforceData } from "./use_workforce_data";

type UseCalendarRootDataReturn = {
  data: WorkforceResponse | ProjectsResponse | undefined;
  pinnedData: WorkforceResponse | ProjectsResponse | undefined;
};

export const useCalendarRootData = (): UseCalendarRootDataReturn => {
  const calendarRange = useBookingModuleStore((state) => state.calendarRange);
  const calendarRoot = useCalendarRoot();
  const { selectedOrder } = useBookingModuleStore((state) => state[calendarRoot].order);
  const tab = useBookingModuleStore((state) => state.tab);
  const page = useBookingModuleStore((state) => state[calendarRoot].page);
  const pinnedIds = useBookingModuleStore((state) => state[calendarRoot].pinnedIds);
  const {
    setLoading,
    [calendarRoot]: { setSelectedOrder, resetPagination }
  } = useBookingModuleStoreActions();

  const { data: workforceData, isFetching: isWorkforceFetching } = useWorkforceData({
    enabled: tab === Tabs.Overview && isWorkforceProfiles(calendarRoot),
    dateRange: calendarRange,
    page,
    selectedOrder,
    setSelectedOrder,
    resetPagination
  });
  const { data: workforcePinnedData, isFetching: isWorkforcePinnedFetching } = usePinnedWorkforceMembers({
    pinnedIds,
    dateRange: calendarRange,
    options: { enabled: isWorkforceProfiles(calendarRoot) }
  });

  const { data: projectsData, isFetching: isProjectsFetching } = useProjectsData({
    enabled: tab === Tabs.Overview && isProjects(calendarRoot),
    calendarRoot,
    page,
    resetPagination,
    selectedOrder,
    setSelectedOrder
  });
  const { data: projectsPinnedData, isFetching: isProjectsPinnedFetching } = usePinnedProjects({
    calendarRoot,
    pinnedIds,
    options: { enabled: isProjects(calendarRoot) }
  });

  useCalendarRootMeta({ workforceData, projectsData });
  useWorkforceMetricsData({ workforceMeta: workforceData?.meta });

  const isLoading =
    isWorkforceFetching || isWorkforcePinnedFetching || isProjectsFetching || isProjectsPinnedFetching;

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return {
    data: isWorkforceProfiles(calendarRoot) ? workforceData : projectsData,
    pinnedData: isWorkforceProfiles(calendarRoot) ? workforcePinnedData : projectsPinnedData
  };
};
